// src/components/Vocabulary/CrosswordModule.tsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CrosswordPuzzle from './CrosswordPuzzle';
import './CrosswordPuzzle.css';

import { 
  getAllPuzzles, 
  getPuzzleById, 
  getPuzzlesByCategory, 
  getPuzzlesByDifficulty,
  recordPuzzleCompletion,
  initializeCrosswordDatabase,
  CrosswordPuzzle as CrosswordPuzzleType
} from '../../firebase/crosswordService';

// SinglePuzzle component for individual puzzle display
const SinglePuzzle: React.FC = () => {
  const { puzzleId } = useParams<{ puzzleId: string }>();
  const navigate = useNavigate();
  const [puzzle, setPuzzle] = useState<CrosswordPuzzleType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showCompletionDialog, setShowCompletionDialog] = useState<boolean>(false);
  const [allPuzzles, setAllPuzzles] = useState<CrosswordPuzzleType[]>([]);

  useEffect(() => {
    async function loadPuzzle() {
      if (!puzzleId) {
        setError('No puzzle ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Load all puzzles for completion recommendations
        const allPuzzlesList = await getAllPuzzles();
        setAllPuzzles(allPuzzlesList);
        
        const puzzleData = await getPuzzleById(puzzleId);
        
        if (puzzleData) {
          setPuzzle(puzzleData);
        } else {
          setError('Puzzle not found');
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading puzzle:', err);
        setError(`Failed to load puzzle: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setLoading(false);
      }
    }

    loadPuzzle();
  }, [puzzleId]);

  const handlePuzzleComplete = async (timeToComplete?: number) => {
    try {
      const userId = 'user123'; // Replace with actual user ID
      
      if (puzzle && puzzle.id) {
        if (timeToComplete !== undefined) {
          await recordPuzzleCompletion(userId, puzzle.id, timeToComplete);
        }
        
        setShowCompletionDialog(true);
      }
    } catch (err) {
      console.error('Error recording puzzle completion:', err);
    }
  };

  const handleCompletionAction = async (action: 'another' | 'new' | 'home') => {
    setShowCompletionDialog(false);
    
    if (action === 'another' && puzzle) {
      // Get another random puzzle from same category and difficulty
      const similarPuzzles = allPuzzles.filter(p => 
        p.category === puzzle.category && 
        p.difficulty === puzzle.difficulty &&
        p.id !== puzzle.id // Don't include current puzzle
      );
      
      if (similarPuzzles.length > 0) {
        const randomPuzzle = similarPuzzles[Math.floor(Math.random() * similarPuzzles.length)];
        if (randomPuzzle.id) {
          navigate(`/crosswords/${randomPuzzle.id}`);
        }
      } else {
        navigate('/crosswords');
      }
    } else if (action === 'new') {
      navigate('/crosswords');
    } else {
      navigate('/crosswords');
    }
  };

  const handleNavigateHome = () => {
    navigate('/crosswords');
  };

  if (loading) {
    return (
      <div className="crossword-loading">
        <div className="spinner"></div>
        <p>Loading crossword puzzle...</p>
      </div>
    );
  }

  if (error || !puzzle) {
    return (
      <div className="crossword-error">
        <h2>Oops! Something went wrong</h2>
        <p>{error || 'Puzzle not found'}</p>
        <button onClick={handleNavigateHome}>Back to Crosswords</button>
      </div>
    );
  }

  return (
    <div>
      <CrosswordPuzzle
        onNavigateHome={handleNavigateHome}
        wordList={puzzle.words}
        onPuzzleComplete={handlePuzzleComplete}
      />
      
      {/* Completion Dialog */}
      {showCompletionDialog && puzzle && (
        <div className="completion-overlay">
          <div className="completion-dialog">
            <h2>🎉 Puzzle Complete! 🎉</h2>
            <p>Great job solving the crossword!</p>
            
            <div className="completion-buttons">
              <button 
                className="completion-btn primary"
                onClick={() => handleCompletionAction('another')}
              >
                Another {puzzle.category} {puzzle.difficulty} puzzle
              </button>
              
              <button 
                className="completion-btn secondary"
                onClick={() => handleCompletionAction('new')}
              >
                Pick new category
              </button>
              
              <button 
                className="completion-btn tertiary"
                onClick={() => handleCompletionAction('home')}
              >
                All done!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main component to select and play crossword puzzles
const CrosswordModule: React.FC<{ onNavigateHome: () => void }> = ({ onNavigateHome }) => {
  const [view, setView] = useState<'selection' | 'puzzle'>('selection');
  const [puzzles, setPuzzles] = useState<CrosswordPuzzleType[]>([]);
  const [allPuzzles, setAllPuzzles] = useState<CrosswordPuzzleType[]>([]);
  const [selectedPuzzle, setSelectedPuzzle] = useState<CrosswordPuzzleType | null>(null);
  const [currentPuzzleIndex, setCurrentPuzzleIndex] = useState<number>(-1);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showCompletionDialog, setShowCompletionDialog] = useState<boolean>(false);
  const [filter, setFilter] = useState<{
    category: string;
    difficulty: string;
  }>({
    category: 'all',
    difficulty: 'all'
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [puzzlesPerPage] = useState<number>(12); // Show 12 puzzles per page
  
  const navigate = useNavigate();
  
  // Calculate pagination
  const indexOfLastPuzzle = currentPage * puzzlesPerPage;
  const indexOfFirstPuzzle = indexOfLastPuzzle - puzzlesPerPage;
  const currentPuzzles = puzzles.slice(indexOfFirstPuzzle, indexOfLastPuzzle);
  const totalPages = Math.ceil(puzzles.length / puzzlesPerPage);
  
  // Initialize puzzles on first load
  useEffect(() => {
    async function loadPuzzles() {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Initializing crossword database...');
        await initializeCrosswordDatabase();
        
        console.log('Getting all puzzles...');
        const allPuzzlesList = await getAllPuzzles();
        console.log(`Found ${allPuzzlesList.length} puzzles`);
        
        setAllPuzzles(allPuzzlesList);
        setPuzzles(allPuzzlesList);
        setLoading(false);
      } catch (err) {
        console.error('Error loading puzzles:', err);
        setError(`Failed to load puzzles: ${err instanceof Error ? err.message : 'Unknown error'}. Please try again later.`);
        setLoading(false);
      }
    }
    
    loadPuzzles();
  }, []);
  
  // Filter puzzles when filter changes
  useEffect(() => {
    if (allPuzzles.length === 0) return;
    
    async function filterPuzzles() {
      try {
        setLoading(true);
        setError(null);
        console.log('Filtering puzzles:', filter);
        
        let filteredPuzzles: CrosswordPuzzleType[];
        
        if (filter.category !== 'all' && filter.difficulty !== 'all') {
          filteredPuzzles = allPuzzles.filter(p => 
            p.category === filter.category && p.difficulty === filter.difficulty
          );
        } else if (filter.category !== 'all') {
          filteredPuzzles = allPuzzles.filter(p => p.category === filter.category);
        } else if (filter.difficulty !== 'all') {
          filteredPuzzles = allPuzzles.filter(p => p.difficulty === filter.difficulty);
        } else {
          filteredPuzzles = allPuzzles;
        }
        
        setPuzzles(filteredPuzzles);
        setCurrentPage(1); // Reset to first page when filters change
        setLoading(false);
      } catch (err) {
        console.error('Error filtering puzzles:', err);
        setError(`Failed to filter puzzles: ${err instanceof Error ? err.message : 'Unknown error'}. Please try again.`);
        setLoading(false);
      }
    }
    
    filterPuzzles();
  }, [filter, allPuzzles]);
  
  // Quick pick functions
  const handleQuickPick = (type: 'random' | 'easy' | 'medium' | 'hard') => {
    if (allPuzzles.length === 0) return;
    
    let selectedPuzzles: CrosswordPuzzleType[];
    
    switch (type) {
      case 'random':
        selectedPuzzles = allPuzzles;
        break;
      case 'easy':
      case 'medium':
      case 'hard':
        selectedPuzzles = allPuzzles.filter(p => p.difficulty === type);
        break;
      default:
        selectedPuzzles = allPuzzles;
    }
    
    if (selectedPuzzles.length > 0) {
      const randomPuzzle = selectedPuzzles[Math.floor(Math.random() * selectedPuzzles.length)];
      if (randomPuzzle.id) {
        handlePuzzleSelect(randomPuzzle.id);
      }
    }
  };
  
  // Pagination functions
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Handle puzzle selection
  const handlePuzzleSelect = async (puzzleId: string) => {
    try {
      setLoading(true);
      setError(null);
      const puzzle = await getPuzzleById(puzzleId);
      
      if (puzzle) {
        setSelectedPuzzle(puzzle);
        setView('puzzle');
        
        // Find the index in the current filtered puzzles list
        const index = puzzles.findIndex(p => p.id === puzzleId);
        setCurrentPuzzleIndex(index);
        
        navigate(`/crosswords/${puzzleId}`);
      } else {
        setError('Puzzle not found');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error selecting puzzle:', err);
      setError(`Failed to load the selected puzzle: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setLoading(false);
    }
  };
  
  // Handle returning to puzzle selection
  const handleNavigateHome = () => {
    setView('selection');
    setSelectedPuzzle(null);
    setCurrentPuzzleIndex(-1);
    navigate('/crosswords');
  };
  
  // Handle navigation to next puzzle
  const handleNavigateNext = () => {
    if (currentPuzzleIndex < puzzles.length - 1) {
      const nextPuzzle = puzzles[currentPuzzleIndex + 1];
      if (nextPuzzle.id) {
        handlePuzzleSelect(nextPuzzle.id);
      }
    }
  };
  
  // Handle navigation to previous puzzle
  const handleNavigatePrevious = () => {
    if (currentPuzzleIndex > 0) {
      const prevPuzzle = puzzles[currentPuzzleIndex - 1];
      if (prevPuzzle.id) {
        handlePuzzleSelect(prevPuzzle.id);
      }
    }
  };
  
  // Handle puzzle completion
  const handlePuzzleComplete = async (timeToComplete?: number) => {
    try {
      const userId = 'user123'; // Replace with actual user ID
      
      if (selectedPuzzle && selectedPuzzle.id) {
        if (timeToComplete !== undefined) {
          await recordPuzzleCompletion(userId, selectedPuzzle.id, timeToComplete);
        }
        
        setShowCompletionDialog(true);
      }
    } catch (err) {
      console.error('Error recording puzzle completion:', err);
    }
  };

  // Handle completion dialog actions
  const handleCompletionAction = async (action: 'another' | 'new' | 'home') => {
    setShowCompletionDialog(false);
    
    if (action === 'another' && selectedPuzzle) {
      // Get another random puzzle from same category and difficulty
      const similarPuzzles = allPuzzles.filter(p => 
        p.category === selectedPuzzle.category && 
        p.difficulty === selectedPuzzle.difficulty &&
        p.id !== selectedPuzzle.id // Don't include current puzzle
      );
      
      if (similarPuzzles.length > 0) {
        const randomPuzzle = similarPuzzles[Math.floor(Math.random() * similarPuzzles.length)];
        if (randomPuzzle.id) {
          handlePuzzleSelect(randomPuzzle.id);
        }
      } else {
        handleNavigateHome();
      }
    } else if (action === 'new') {
      handleNavigateHome();
    } else {
      handleNavigateHome();
    }
  };
  
  // Handle category filter change
  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    console.log('Category changed to:', e.target.value);
    setFilter({
      ...filter,
      category: e.target.value
    });
  };
  
  // Handle difficulty filter change
  const handleDifficultyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    console.log('Difficulty changed to:', e.target.value);
    setFilter({
      ...filter,
      difficulty: e.target.value
    });
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="crossword-loading">
        <div className="spinner"></div>
        <p>Loading crossword puzzles...</p>
      </div>
    );
  }
  
  // Error state with more details
  if (error) {
    return (
      <div className="crossword-error">
        <h2>Oops! Something went wrong</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Try Again</button>
        <button onClick={onNavigateHome} style={{ marginLeft: '10px' }}>Go to Home</button>
      </div>
    );
  }
  
  // Render puzzle selection screen
  if (view === 'selection') {
    // Define categories with icons and difficulty options
    const categories = [
      { name: 'Animals', icon: '🐾', color: '#4CAF50' },
      { name: 'Food', icon: '🍎', color: '#FF9800' },
      { name: 'Sports', icon: '🏀', color: '#2196F3' },
      { name: 'School', icon: '📚', color: '#9C27B0' },
      { name: 'Colors', icon: '🎨', color: '#F44336' },
      { name: 'Nature', icon: '🌳', color: '#8BC34A' },
      { name: 'Body', icon: '👋', color: '#FF5722' },
      { name: 'Clothing', icon: '👕', color: '#607D8B' },
      { name: 'Transportation', icon: '🚗', color: '#00BCD4' },
      { name: 'Weather', icon: '⛅', color: '#03A9F4' }
    ];

    const difficulties = [
      { level: 'easy', label: 'Easy', icon: '😊', color: '#8BC34A' },
      { level: 'medium', label: 'Medium', icon: '🤔', color: '#FFC107' },
      { level: 'hard', label: 'Hard', icon: '🤓', color: '#e91e63' }
    ];

    return (
      <div className="crossword-selection">
        <h1>Pick Your Crossword!</h1>
        
        {/* Quick Pick Section */}
        <div className="quick-pick-section">
          <h2>Quick Pick!</h2>
          <div className="quick-pick-buttons">
            <button 
              className="quick-pick-btn random"
              onClick={() => handleQuickPick('random')}
            >
              🎲 Surprise Me!
            </button>
            {difficulties.map(diff => (
              <button 
                key={diff.level}
                className={`quick-pick-btn ${diff.level}`}
                style={{ background: diff.color }}
                onClick={() => handleQuickPick(diff.level as 'easy' | 'medium' | 'hard')}
              >
                {diff.icon} {diff.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Categories Grid */}
        <div className="categories-grid">
          {categories.map((category) => (
            <div 
              key={category.name}
              className="category-card"
              style={{ borderColor: category.color }}
            >
              <div className="category-icon" style={{ color: category.color }}>
                {category.icon}
              </div>
              <h3>{category.name}</h3>
              <div className="difficulty-options">
                {difficulties.map(diff => (
                  <button
                    key={diff.level}
                    className="difficulty-btn"
                    style={{ backgroundColor: diff.color }}
                    onClick={() => {
                      // Filter puzzles by category and difficulty, then pick random one
                      const filteredPuzzles = allPuzzles.filter(p => 
                        p.category.toLowerCase() === category.name.toLowerCase() && 
                        p.difficulty === diff.level
                      );
                      if (filteredPuzzles.length > 0) {
                        const randomPuzzle = filteredPuzzles[Math.floor(Math.random() * filteredPuzzles.length)];
                        if (randomPuzzle.id) {
                          handlePuzzleSelect(randomPuzzle.id);
                        }
                      }
                    }}
                  >
                    {diff.icon}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        
        {/* Remove pagination and individual puzzle display */}
      </div>
    );
  }
  
  // Render the crossword puzzle with navigation
  return (
    <div className="crossword-game">
      {selectedPuzzle && (
        <>
          {/* Navigation buttons */}
          <div className="puzzle-navigation">
            <button 
              onClick={handleNavigatePrevious}
              disabled={currentPuzzleIndex <= 0}
              className="nav-btn prev-btn"
            >
              ← Previous Puzzle
            </button>
            
            <button 
              onClick={handleNavigateHome}
              className="nav-btn home-btn"
            >
              ← Back to Selection
            </button>
            
            <button 
              onClick={handleNavigateNext}
              disabled={currentPuzzleIndex >= puzzles.length - 1}
              className="nav-btn next-btn"
            >
              Next Puzzle →
            </button>
          </div>
          
          {/* Puzzle header info */}
          <div className="puzzle-header">
            <h1>{selectedPuzzle.title}</h1>
            <div className="puzzle-header-info">
              <span className="puzzle-category">{selectedPuzzle.category}</span>
              <span className={`puzzle-difficulty ${selectedPuzzle.difficulty}`}>
                {selectedPuzzle.difficulty.charAt(0).toUpperCase() + selectedPuzzle.difficulty.slice(1)}
              </span>
              <span className="puzzle-progress">
                Puzzle {currentPuzzleIndex + 1} of {puzzles.length}
              </span>
            </div>
          </div>
          
          {/* The actual crossword puzzle component */}
          <CrosswordPuzzle
            onNavigateHome={handleNavigateHome}
            wordList={selectedPuzzle.words}
            onPuzzleComplete={handlePuzzleComplete}
          />
          
          {/* Optional footer navigation for mobile */}
          <div className="puzzle-navigation mobile-navigation">
            <button 
              onClick={handleNavigatePrevious}
              disabled={currentPuzzleIndex <= 0}
              className="nav-btn prev-btn mobile"
            >
              ←
            </button>
            
            <button 
              onClick={handleNavigateHome}
              className="nav-btn home-btn mobile"
            >
              Home
            </button>
            
            <button 
              onClick={handleNavigateNext}
              disabled={currentPuzzleIndex >= puzzles.length - 1}
              className="nav-btn next-btn mobile"
            >
              →
            </button>
          </div>
        </>
      )}
      
      {/* Completion Dialog */}
      {showCompletionDialog && selectedPuzzle && (
        <div className="completion-overlay">
          <div className="completion-dialog">
            <h2>🎉 Puzzle Complete! 🎉</h2>
            <p>Great job solving the crossword!</p>
            
            <div className="completion-buttons">
              <button 
                className="completion-btn primary"
                onClick={() => handleCompletionAction('another')}
              >
                Another {selectedPuzzle.category} {selectedPuzzle.difficulty} puzzle
              </button>
              
              <button 
                className="completion-btn secondary"
                onClick={() => handleCompletionAction('new')}
              >
                Pick new category
              </button>
              
              <button 
                className="completion-btn tertiary"
                onClick={() => handleCompletionAction('home')}
              >
                All done!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrosswordModule;
export { SinglePuzzle };